import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useParams } from 'react-router-dom';
import {
  Button,
  Menu,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  mergeClasses,
  tokens,
  Caption1,
  Subtitle2,
  Tooltip,
} from '@fluentui/react-components';
import { SettingsRegular } from '@fluentui/react-icons';
import { SignInContext, AuthContext, Avatar, Guard } from '@modules/users';
import { Path } from '@shared/components';
import { TPath } from '@modules/items';
import { TIcon } from '@shared/types';
import { useFolderPathAsBreadcrumbs } from '../hooks';
import { SearchBar } from '@modules/search';
import {
  AiAssistantButton,
  AiAssistantOverlay,
  TAiAssistantOrigin,
  requestAiAssistantClose,
  useClearGroundedQaScrollParams,
  useCanUseAiQa,
} from '@modules/ai';

const useStyles = makeStyles({
  // Pages with the shortcuts rail frame their content in a white shell, like Microsoft 365 apps.
  shell: {
    ':has([data-shortcuts-rail])': {
      backgroundColor: tokens.colorNeutralBackground1,
    },
  },
  home: {
    '& .fui-Button__icon': {
      width: '28px',
      height: '28px',
    },
    height: '48px',
  },
  iconButton: {
    width: '48px',
    maxWidth: '48px',
    height: '48px',
  },
  logout: {
    minWidth: 0,
  },

  name: {
    height: '48px',
    cursor: 'text !important',
  },
});

const ProfileCircle = () => {
  const { logout } = useContext(AuthContext);
  const { user } = useContext(SignInContext);

  const classes = useStyles();
  return (
    <Menu>
      <MenuTrigger>
        <Button
          size="large"
          appearance="subtle"
          className={classes.iconButton}
          icon={<Avatar user={user} color="brand" />}
        />
      </MenuTrigger>

      <MenuPopover>
        <div className="flex justify-end">
          <Button appearance="subtle" onClick={logout} className={classes.logout}>
            Sign out
          </Button>
        </div>
        <div className="p-2 flex space-x-4 items-center">
          <Avatar user={user} color="brand" size={48} />
          <div className="flex flex-col">
            <Subtitle2>{user.name}</Subtitle2>
            <Caption1>{user.mail}</Caption1>
          </div>
        </div>
      </MenuPopover>
    </Menu>
  );
};

const LayoutPath = ({ resolvedPath }: { resolvedPath: TPath }) => {
  return <Path items={useFolderPathAsBreadcrumbs(resolvedPath)} size="small" hasCurrent={false} />;
};

type TLayoutState = {
  _id: string;
  name: string;
  icon: TIcon;
  resolvedPath: TPath;
  onRename: () => void;
};

type TLayoutContext = {
  layoutState: TLayoutState | undefined;
  setLayoutState: (state: TLayoutState) => void;
  currentOrigin: TAiAssistantOrigin | undefined;
  setCurrentOrigin: (target: TAiAssistantOrigin | undefined) => void;
};

// @ts-ignore
export const LayoutContext = createContext<TLayoutContext>();

export const LayoutProvider = () => {
  const { id } = useParams();
  const [layoutState, setLayoutState] = useState<TLayoutState | undefined>();
  const [currentOrigin, setCurrentOrigin] = useState<TAiAssistantOrigin>();
  const [isAiOpen, setIsAiOpen] = useState(false);
  const clearGroundedQaScrollParams = useClearGroundedQaScrollParams();
  const canUseAiQa = useCanUseAiQa();

  useEffect(() => {
    if (!canUseAiQa && isAiOpen) setIsAiOpen(false);
  }, [isAiOpen, canUseAiQa]);

  const classes = useStyles();
  const contextValue = useMemo(
    () => ({ layoutState, setLayoutState, currentOrigin, setCurrentOrigin }),
    [layoutState, currentOrigin]
  );
  return (
    <div className={mergeClasses(classes.shell, 'h-full flex flex-col')}>
      <div className="w-full flex justify-between items-center">
        <Link to="/">
          <Button appearance="subtle" size="large" icon={<img src="/favicon/svg.svg" />} className={classes.home}>
            Confideck
          </Button>
        </Link>

        {layoutState && layoutState._id === id ? (
          <div className="flex items-center -ml-8">
            <div className="flex justify-end items-center" style={{ width: '300px', paddingLeft: '50px' }}>
              <Button
                size={layoutState.name.length > 25 ? 'medium' : 'large' /* is also used below */}
                appearance="transparent"
                className={classes.name}
                onClick={layoutState.onRename}
                icon={layoutState.icon}
              >
                <Tooltip
                  content={layoutState.name.length > 28 ? layoutState.name : 'Rename'}
                  relationship="label"
                  withArrow
                >
                  <p className="text-ellipsis overflow-hidden text-nowrap py-1.5">{layoutState.name}</p>
                </Tooltip>
              </Button>
            </div>

            <div
              style={{
                width: '300px',
                marginTop: layoutState.name.length > 25 ? '0.1rem' : '0.12rem' /* is also used above */,
              }}
            >
              <LayoutPath resolvedPath={layoutState.resolvedPath} />
            </div>
          </div>
        ) : (
          <div className="flex flex-row items-center w-full max-w-xl">
            <SearchBar />
          </div>
        )}

        <div className="flex items-center gap-2 mr-2">
          {canUseAiQa && !(layoutState && layoutState._id === id) && (
            <AiAssistantButton
              size="medium"
              checked={isAiOpen}
              onClick={() => {
                if (isAiOpen) requestAiAssistantClose('overlay', () => setIsAiOpen(false));
                else setIsAiOpen(true);
              }}
            />
          )}
          <Guard onlyAdmins>
            <Link to="/settings">
              <Button appearance="subtle" icon={<SettingsRegular />} title="Settings" className={classes.iconButton} />
            </Link>
          </Guard>
          <ProfileCircle />
        </div>
      </div>

      <LayoutContext.Provider value={contextValue}>
        <Outlet />
        <AiAssistantOverlay
          isOpen={canUseAiQa && isAiOpen}
          onClose={() => {
            clearGroundedQaScrollParams();
            setIsAiOpen(false);
          }}
          origin={currentOrigin}
        />
      </LayoutContext.Provider>
    </div>
  );
};
